import axios from 'axios';
import {
  checkIsInternal,
  fetchInternalAccessToken,
  getUserIdFromLoggedUser,
  fetchAccessToken,
} from './createStorage';
import { environment, apiUrls } from '@src/config';

let refreshTokenPromise = null;

const BULK_EXPORT_HOST = 'bulk-exporter.s3';
const BLOB_STORAGE_HOST = 'blob.core.windows.net';

const parseJsonSafely = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch (_e) {
    return fallback;
  }
};

const getCookieValue = (cookieName) => {
  if (!cookieName || typeof document === 'undefined') {
    return null;
  }

  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + cookieName.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );

  return matches ? decodeURIComponent(matches[1]) : null;
};

const getUserType = () => {
  const currentUser = parseJsonSafely(
    typeof window !== 'undefined' ? window.sessionStorage.getItem('currentUser') : null,
    null
  );

  const userType = currentUser?.[0]?.UserPersonas?.[0]?.UserType;
  return userType || (checkIsInternal() ? 'Internal' : 'External');
};

const getExternalUserId = () => {
  const loggedUser = parseJsonSafely(
    typeof window !== 'undefined' ? window.sessionStorage.getItem('loggedUser') : null,
    null
  );

  if (!loggedUser) {
    return '';
  }

  if (Array.isArray(loggedUser)) {
    return loggedUser[0]?.OptumId || loggedUser[0]?.MsId || '';
  }

  return loggedUser.OptumId || loggedUser.MsId || '';
};

const isOhidAuthEndpoint = (url = '') => {
  return (
    url.includes('/api/auth/ohid/login') ||
    url.includes('/api/auth/ohid/callback') ||
    url.includes('/api/auth/ohid/logout') ||
    url.includes('/api/auth/ohid/refresh')
  );
};

const resolveExternalAccessToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const uhcCurrentUser = parseJsonSafely(window.sessionStorage.getItem('UHC_CURRENT_USER'), null);
  if (uhcCurrentUser?.AccessToken) {
    return uhcCurrentUser.AccessToken;
  }

  const currentUser = parseJsonSafely(window.sessionStorage.getItem('currentUser'), null);
  if (Array.isArray(currentUser) && currentUser[0]?.AccessToken) {
    return currentUser[0].AccessToken;
  }

  try {
    const token = fetchAccessToken();
    if (token) {
      return token;
    }
  } catch (_e) {
    // Ignore fallback parse errors.
  }

  return null;
};

const resolveInternalAccessToken = () => {
  const token = fetchInternalAccessToken();
  if (token) {
    return token;
  }

  const prefixedCookieToken = getCookieValue(`${environment.cookieEnvironment || ''}ACCESS_TOKEN`);
  if (prefixedCookieToken) {
    return prefixedCookieToken;
  }

  return getCookieValue('ACCESS_TOKEN');
};

const getContentType = (requestUrl = '', getTrendUrl, loggedUserMsId) => {
  const goldCardApi = environment?.goldCardApi;
  const isOhidCallback = requestUrl.indexOf(apiUrls.OHIDCallback) !== -1;
  const isLdapAuth = requestUrl.indexOf(apiUrls.LDAPAuth) !== -1;

  if (((goldCardApi && requestUrl.indexOf(goldCardApi) !== 0) || !goldCardApi) && isOhidCallback) {
    return 'application/x-www-form-urlencoded';
  }

  if (isLdapAuth) {
    return 'application/x-www-form-urlencoded';
  }

  if (requestUrl.indexOf(getTrendUrl + loggedUserMsId) === -1) {
    return 'application/json';
  }

  return '';
};

const setExternalTokens = (newAccessToken, newRefreshToken, newIdToken) => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUser = parseJsonSafely(window.sessionStorage.getItem('currentUser'), null);
  if (Array.isArray(currentUser) && currentUser[0]) {
    currentUser[0].AccessToken = newAccessToken || currentUser[0].AccessToken;
    currentUser[0].RefreshToken = newRefreshToken || currentUser[0].RefreshToken;
    if (newIdToken) {
      currentUser[0].IdToken = newIdToken;
    }
    window.sessionStorage.setItem('currentUser', JSON.stringify(currentUser));
  }

  const uhcCurrentUser = parseJsonSafely(window.sessionStorage.getItem('UHC_CURRENT_USER'), null);
  if (uhcCurrentUser && typeof uhcCurrentUser === 'object') {
    const nextUser = {
      ...uhcCurrentUser,
      AccessToken: newAccessToken || uhcCurrentUser.AccessToken,
      RefreshToken: newRefreshToken || uhcCurrentUser.RefreshToken,
      ...(newIdToken ? { IdToken: newIdToken } : {}),
    };
    window.sessionStorage.setItem('UHC_CURRENT_USER', JSON.stringify(nextUser));
  }

  if (newAccessToken) {
    window.sessionStorage.setItem('accessToken', newAccessToken);
  }
};

const refreshExternalToken = async (instance) => {
  const response = await instance.post(apiUrls.OHIDTokenRefresh, null, {
    _skipAuthRefresh: true,
  });

  const payload = response?.data?.body || response?.data || {};
  const newAccessToken = payload.access_token || payload.Authorization;
  const newRefreshToken = payload.refresh_token || payload.RefreshToken;
  const newIdToken = payload.id_token || payload.IdToken;

  if (!newAccessToken) {
    throw new Error('Token refresh succeeded without an access token');
  }

  setExternalTokens(newAccessToken, newRefreshToken, newIdToken);
  return newAccessToken;
};

const getRequestHostname = (requestUrl, apiDomain) => {
  if (!requestUrl) {
    return null;
  }
  if (typeof window === 'undefined' || !window.URL) {
    return null;
  }
  try {
    const URLCtor = window.URL;
    const urlObject = requestUrl.startsWith('http://') || requestUrl.startsWith('https://')
      ? new URLCtor(requestUrl)
      : new URLCtor(requestUrl, apiDomain);
    return urlObject.hostname;
  } catch (_e) {
    return null;
  }
};

export const setupTokenInterceptor = (instance, { apiDomain, getAbortSignal }) => {
  instance.interceptors.request.use((requestConfig) => {
    if (typeof window !== 'undefined' && window.sessionStorage.getItem('logout') === 'true') {
      return Promise.reject(new axios.CanceledError('Request blocked: user already logged out'));
    }

    requestConfig.headers = requestConfig.headers || {};

    if (getAbortSignal && !requestConfig.signal) {
      const signal = getAbortSignal();
      if (signal) {
        requestConfig.signal = signal;
      }
    }

    if (requestConfig?._skipAuthInterceptor) {
      return requestConfig;
    }

    if (
      environment?.toggleServer &&
      environment?.devEnvExternal &&
      requestConfig?.url?.includes(environment.devEnvExternal) &&
      !requestConfig.url.includes('code=')
    ) {
      requestConfig.url = requestConfig.url.replace(environment.devEnvExternal, '');
    }

    const isInternal = checkIsInternal();
    const currentUser = parseJsonSafely(
      typeof window !== 'undefined' ? window.sessionStorage.getItem('currentUser') : null,
      null
    );
    const loggedUser = parseJsonSafely(
      typeof window !== 'undefined' ? window.sessionStorage.getItem('loggedUser') : null,
      null
    );

    const loggedUserMsId = Array.isArray(loggedUser)
      ? loggedUser[0]?.MsId
        ? `/${loggedUser[0].MsId}`
        : ''
      : loggedUser?.MsId
        ? `/${loggedUser.MsId}`
        : '';

    const getTrendUrl = 'api/getTrendAccess';
    const refreshTokenUrl = apiUrls.SsoTokenRefresh;
    const requestUrl = requestConfig?.url || '';
    const isLoginLogoutUrl = requestUrl.includes('login') || requestUrl.includes('logout');
    const isOhidEndpoint = isOhidAuthEndpoint(requestUrl);

    const tokenToAppend = isInternal
      ? resolveInternalAccessToken()
      : resolveExternalAccessToken();

    const userId = isInternal ? getUserIdFromLoggedUser() : getExternalUserId();
    const userType = getUserType();

    if (String(requestConfig.method || 'get').toUpperCase() === 'POST' && environment?.XSRFToggle) {
      const xsrfToken =
        getCookieValue(`${environment.cookieEnvironment || ''}XSRF-TOKEN`) ||
        getCookieValue('XSRF-TOKEN');
      if (xsrfToken) {
        requestConfig.headers['X-XSRF-TOKEN'] = xsrfToken;
        requestConfig.withCredentials = true;
      }
    }

    if (isInternal) {
      const shouldAttachInternalToken =
        !isLoginLogoutUrl &&
        requestUrl.indexOf(getTrendUrl + loggedUserMsId) === -1 &&
        requestUrl.indexOf(refreshTokenUrl + loggedUserMsId) === -1 &&
        requestUrl.indexOf(environment?.apiUrls?.performanceExportS3url) === -1;

      const shouldAttachForFetchUser = requestUrl.indexOf(environment?.apiUrls?.SsoFetchUserDetails) > -1;
      if ((shouldAttachInternalToken && currentUser) || shouldAttachForFetchUser) {
        requestConfig.headers.Authorization = tokenToAppend ? `Bearer ${tokenToAppend}` : '';
      }

      requestConfig.headers['x-idp'] = 'azuread';
      requestConfig.headers['X-Upstream-Env'] = environment.upstreamEnv;
      requestConfig.headers['x-upstream-env'] = environment.upstreamEnv;
      requestConfig.headers.UserType = userType;
      if (userId) {
        requestConfig.headers.UserId = userId;
        requestConfig.headers.userId = userId;
      }
      if (requestConfig?.headers?.Cookie) {
        delete requestConfig.headers.Cookie;
      }
    } else {
      if (!isOhidEndpoint) {
        requestConfig.headers.Authorization = tokenToAppend ? `Bearer ${tokenToAppend}` : '';
      }

      requestConfig.headers['x-idp'] = 'onehealthcareid';
      requestConfig.headers['X-Upstream-Env'] = environment.upstreamEnv;
      requestConfig.headers['x-upstream-env'] = environment.upstreamEnv;
      requestConfig.headers.UserType = userType;
      if (userId) {
        requestConfig.headers.UserId = userId;
      }
    }

    requestConfig.headers.Accept = '*/*';
    requestConfig.headers.usertype = userType;

    const contentType = getContentType(requestUrl, getTrendUrl, loggedUserMsId);
    if (contentType) {
      requestConfig.headers['Content-Type'] = contentType;
    }

    const requestHostname = getRequestHostname(requestConfig?.url, apiDomain);
    const isBulkExportHost = requestHostname && requestHostname.includes(BULK_EXPORT_HOST);
    const isBlobStorageHost =
      requestHostname &&
      (requestHostname === BLOB_STORAGE_HOST || requestHostname.endsWith(`.${BLOB_STORAGE_HOST}`));
    if (isBulkExportHost || isBlobStorageHost) {
      delete requestConfig?.headers?.Authorization;
    }

    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const status = error?.response?.status;
      const originalRequest = error?.config;
      const isInternal = checkIsInternal();
      const shouldSkipRefresh = originalRequest?._skipAuthRefresh;
      const requestUrl = originalRequest?.url || '';

      if (
        status === 401 &&
        !isInternal &&
        !shouldSkipRefresh &&
        !isOhidAuthEndpoint(requestUrl) &&
        !originalRequest?._retry
      ) {
        try {
          if (!refreshTokenPromise) {
            refreshTokenPromise = refreshExternalToken(instance).finally(() => {
              refreshTokenPromise = null;
            });
          }

          const refreshedAccessToken = await refreshTokenPromise;
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;

          return instance(originalRequest);
        } catch (refreshError) {
          refreshTokenPromise = null;
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('logout', 'true');
            window.dispatchEvent(new CustomEvent('signout'));
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
